/**
 * CapabilityCardsBlock: `capability-cards` DYNAMIC content block.
 *
 * data: { sectionHeading?, hiddenCards? }
 *
 * Renders a Bootstrap card grid of platform capabilities. Which cards appear
 * is determined by two layers:
 *   1. Per-ecosystem feature flags via useEcosystem() — the ceiling.
 *   2. Per-block hiddenCards array from the admin editor — opt-out within
 *      what the flags allow.
 *
 * Adding a new card requires only a CARD_CATALOG entry in
 * capabilityCardsConfig.js. No hook calls or flag logic updates needed.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import useIsFlagEnabled from '@/shared/hooks/useIsFlagEnabled';

import CARD_CATALOG from './capabilityCardsConfig';

export default function CapabilityCardsBlock({ data }) {
    const d = data || {};
    const heading = d.sectionHeading;
    const hiddenCards = Array.isArray(d.hiddenCards) ? d.hiddenCards : [];

    const isFlagEnabled = useIsFlagEnabled();

    const visibleCards = CARD_CATALOG.filter((card) => {
        if (!isFlagEnabled(card.featureKey)) return false;
        if (hiddenCards.includes(card.id)) return false;
        return true;
    });

    if (visibleCards.length === 0) return null;

    return (
        <>
            {heading && (
                <h2 className="content-heading--h2 text-center text-primary mb-4">
                    {heading}
                </h2>
            )}
            <Row className="g-4 justify-content-center">
                {visibleCards.map((card) => (
                    <Col key={card.id} md={6} lg={3}>
                        <Card className="h-100 border-0 shadow-soft">
                            <Card.Body className="text-center p-4 d-flex flex-column">
                                <div className="feature-icon mb-3">
                                    {card.icon}
                                </div>
                                <h4 className="text-primary">{card.heading}</h4>
                                <p className="text-muted">{card.description}</p>
                                <Button
                                    as={Link}
                                    to={card.ctaPath}
                                    variant="outline-primary"
                                    size="sm"
                                    className="rounded-pill mt-auto"
                                >
                                    {card.ctaLabel}
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>
        </>
    );
}

CapabilityCardsBlock.propTypes = {
    data: PropTypes.shape({
        sectionHeading: PropTypes.string,
        hiddenCards: PropTypes.arrayOf(PropTypes.string),
    }),
};

CapabilityCardsBlock.defaultProps = {
    data: {},
};
